import { Box, Typography, useTheme } from '@mui/material';
import { FC, ReactNode, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { defaultAnimation, formatRupeesIndianCompact, premiumTooltip } from '../Reports/echartsConfig';

export interface BookingsDonutSlice {
  statusKey: string;
  label: string;
  value: number;
  color: string;
  amount?: number;
}

interface DonutChartProps {
  items: BookingsDonutSlice[];
  centerLabel: string;
  centerSub: string;
  onSegmentClick?: (statusKey: string) => void;
  /** When true, chart fills its parent (parent should constrain size, e.g. via container queries). */
  fill?: boolean;
}

const OUTER_SIZE = 280;
const EMPTY_RING_PX = 22;
const PIE_RADIUS: [string, string] = ['36%', '88%'];

const CenterHub: FC<{ label: string; sub: string }> = ({ label, sub }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 100,
        height: 100,
        borderRadius: '50%',
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: '0 4px 18px rgba(15, 23, 42, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        pointerEvents: 'none',
        px: 1,
      }}>
      <Typography
        sx={{
          fontSize: 38,
          fontWeight: 800,
          lineHeight: 1,
          letterSpacing: -0.5,
          fontVariantNumeric: 'tabular-nums',
          color: 'text.primary',
        }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 11.5,
          color: 'text.secondary',
          fontWeight: 700,
          mt: 0.6,
          letterSpacing: 0.2,
          lineHeight: 1.2,
        }}>
        {sub}
      </Typography>
    </Box>
  );
};

const ChartShell: FC<{ children: ReactNode; isEmpty?: boolean; fill?: boolean }> = ({
  children,
  isEmpty,
  fill,
}) => (
  <Box
    sx={{
      width: fill ? '100%' : OUTER_SIZE,
      height: fill ? '100%' : OUTER_SIZE,
      flexShrink: 0,
      boxSizing: 'border-box',
      p: 1,
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      background: isEmpty
        ? 'linear-gradient(145deg, #f8fafc 0%, #ffffff 100%)'
        : 'linear-gradient(145deg, #fafafa 0%, #ffffff 50%, #f8fafc 100%)',
      boxShadow: '0 2px 12px rgba(15, 23, 42, 0.05)',
      position: 'relative',
      overflow: 'hidden',
    }}>
    <Box
      sx={{
        position: 'absolute',
        inset: '18%',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(25, 118, 210, 0.07) 0%, transparent 72%)',
        pointerEvents: 'none',
      }}
    />
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>{children}</Box>
  </Box>
);

const DonutChart: FC<DonutChartProps> = ({ items, centerLabel, centerSub, onSegmentClick, fill }) => {
  const option = useMemo(() => {
    const pieData = items.map(item => ({
      name: item.label,
      value: item.value,
      itemStyle: {
        color: item.color,
        borderColor: '#fff',
        borderWidth: 2.5,
        borderRadius: 6,
        shadowBlur: 4,
        shadowColor: 'rgba(15, 23, 42, 0.12)',
      },
      statusKey: item.statusKey,
      amount: item.amount,
    }));

    const { axisPointer: _omitAxisPointer, ...tooltipBase } = premiumTooltip;

    return {
      ...defaultAnimation,
      animationDuration: 450,
      animationEasing: 'cubicOut',
      tooltip: {
        ...tooltipBase,
        trigger: 'item',
        enterable: true,
        confine: true,
        formatter: (params: {
          name: string;
          value: number;
          percent: number;
          data?: { statusKey?: string; amount?: number };
        }) => {
          const { name, value, percent } = params;
          const amount = params.data?.amount;
          const canNavigate = Boolean(onSegmentClick && params.data?.statusKey && value > 0);
          const amtLine =
            typeof amount === 'number' && amount > 0
              ? `<div style="margin-top:6px;color:#94a3b8;font-size:11px">Amount <span style="color:#e2e8f0;font-weight:600">${formatRupeesIndianCompact(
                  amount,
                )}</span></div>`
              : '';
          const actionLine = canNavigate
            ? `<div style="margin-top:10px;padding-top:10px;border-top:1px solid rgba(148,163,184,0.25);color:#7dd3fc;font-weight:700;font-size:11px;cursor:pointer">Click to view</div>`
            : '';
          return (
            `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:6px">${name}</div>` +
            `<div style="color:#cbd5e1;font-size:11px"><span style="color:#94a3b8">Bookings</span> ` +
            `<span style="font-weight:700;font-variant-numeric:tabular-nums">${value}</span>` +
            ` <span style="color:#64748b">(${Number(percent).toFixed(1)}%)</span></div>` +
            amtLine +
            actionLine
          );
        },
      },
      series: [
        {
          type: 'pie',
          radius: PIE_RADIUS,
          center: ['50%', '50%'],
          padAngle: 1.2,
          minAngle: 2,
          avoidLabelOverlap: true,
          label: { show: false },
          labelLine: { show: false },
          cursor: onSegmentClick ? 'pointer' : 'default',
          emphasis: {
            scale: true,
            scaleSize: 10,
            itemStyle: {
              shadowBlur: 16,
              shadowOffsetX: 0,
              shadowColor: 'rgba(15, 23, 42, 0.22)',
            },
          },
          data: pieData,
        },
      ],
    };
  }, [items, onSegmentClick]);

  const onEvents = useMemo(
    () => ({
      click: (p: { data?: { statusKey?: string }; value?: number }) => {
        const key = p?.data?.statusKey;
        if (key && onSegmentClick && (p?.value ?? 0) > 0) {
          onSegmentClick(key);
        }
      },
    }),
    [onSegmentClick],
  );

  if (!items.length) {
    return (
      <ChartShell isEmpty fill={fill}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            boxSizing: 'border-box',
            border: `${EMPTY_RING_PX}px solid`,
            borderColor: 'grey.200',
            background: 'conic-gradient(from 0deg, #f1f5f9, #e2e8f0, #f1f5f9)',
          }}
        />
        <CenterHub label={centerLabel} sub={centerSub} />
      </ChartShell>
    );
  }

  return (
    <ChartShell fill={fill}>
      <ReactECharts
        option={option}
        style={{ width: '100%', height: '100%' }}
        opts={{ renderer: 'canvas' }}
        notMerge
        onEvents={onEvents}
      />
      <CenterHub label={centerLabel} sub={centerSub} />
    </ChartShell>
  );
};

export default DonutChart;
export const DONUT_CHART_OUTER_SIZE = OUTER_SIZE;
